import { useEffect, useRef, useState } from "react";
import { Flex, Select, Tooltip } from "antd";
import { usePlayback } from "../../../../context/VideoPlayback";
import {
  RiDownload2Fill,
  RiForward10Fill,
  RiReplay10Fill,
} from "react-icons/ri";
import Button from "../../../Helpers/Button/Button";
import { FaLock, FaLockOpen, FaPause, FaPlay } from "react-icons/fa6";
import { formatTime } from "../../../../utils";

const GlobalTimeline = () => {
  const {
    currentTime,
    setCurrentTime,
    isPlaying,
    setIsPlaying,
    notifications,
    duration,
    selectedFeeds,
    setPlaybackRate,
    sync,
    setSync,
  } = usePlayback();

  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  const timelineRef = useRef(null);

  const progress = (currentTime / duration) * 100;

  const handleScrub = (e) => {
    if (duration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const scrubPercent = offsetX / rect.width;
    const scrubTime = Math.max(0, Math.min(scrubPercent * duration, duration));
    setCurrentTime(scrubTime);
  };

  const handleMouseMove = (e) => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const percent = offsetX / rect.width;
    const time = percent * duration;
    setHoverTime(time);
    setHoverPosition(offsetX);
  };

  const handleMouseLeave = () => {
    setHoverTime(null);
    setHoverPosition(null);
  };

  if (selectedFeeds.length === 0) return null;

  return (
    <Flex className="timeline-container p-3" vertical gap={"small"}>
      {/* Controls */}
      <Flex vertical={false} gap="small" justify="space-between">
        <Button
          onClick={() => setSync(() => (sync ? false : true))}
          icon={sync ? <FaLock size={22} /> : <FaLockOpen size={22} />}
          tooltip={sync ? "Unsync Videos" : "Sync All Videos"}
          type={"default"}
        />
        <Flex vertical={false} gap="small" justify="center">
          <Button
            onClick={() => setCurrentTime((prev) => Math.max(prev - 10, 0))}
            icon={<RiReplay10Fill size={25} />}
            tooltip={"Rewind 10s"}
            type={"default"}
          />
          <Button
            icon={isPlaying ? <FaPause size={25} /> : <FaPlay size={25} />}
            type={"default"}
            tooltip={"Play"}
            onClick={() => setIsPlaying(isPlaying ? false : true)}
          />
          <Button
            onClick={() =>
              setCurrentTime((prev) => Math.min(prev + 10, duration))
            }
            icon={<RiForward10Fill size={25} />}
            tooltip={"Forward 10s"}
            type={"default"}
          />
        </Flex>
        <Select
          defaultValue={1}
          style={{ width: 120 }}
          onChange={(val) => setPlaybackRate(val)}
          options={[
            { value: 0.5, label: "0.5x" },
            { value: 1, label: "1x" },
            { value: 1.5, label: "1.5x" },
            { value: 2, label: "2x" },
          ]}
        />
      </Flex>
      {/* Timeline Bar */}
      <div
        className="timeline-bar position-relative"
        onClick={handleScrub}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        ref={timelineRef}
        style={{
          height: "20px",
          backgroundColor: "#eee",
          position: "relative",
          cursor: "pointer",
        }}
      >
        {/* Progress bar */}
        <div
          className="timeline-progress"
          style={{
            width: `${progress}%`,
            backgroundColor: "#0f6936",
            height: "100%",
          }}
        ></div>

        {/* Hover Time Tooltip */}
        {hoverTime !== null && (
          <div
            className="hover-time-tooltip"
            style={{
              position: "absolute",
              top: "-24px",
              left: hoverPosition,
              transform: "translateX(-50%)",
              padding: "2px 6px",
              backgroundColor: "#000",
              color: "#fff",
              fontSize: "12px",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            {formatTime(hoverTime)}
          </div>
        )}

        {/* Notification Markers */}
        {notifications.map((note, idx) => {
          const markerPosition = (note.time / duration) * 100;
          return (
            <Tooltip title={note.message} key={idx}>
              <div
                className="timeline-marker position-absolute"
                style={{
                  left: `${markerPosition}%`,
                  top: 0,
                  width: "4px",
                  height: "100%",
                  backgroundColor: note.type === "accident" ? "red" : "yellow",
                }}
              ></div>
            </Tooltip>
          );
        })}
      </div>

      {/* Time Labels */}
      <Flex justify="space-between" style={{ marginTop: "4px" }}>
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </Flex>
    </Flex>
  );
};

export default GlobalTimeline;
